/**
 * PTL Earnings Confetti Pop — globally-mounted micro-celebration.
 *
 * Listens for the `ptl:earned` window event dispatched by AudioContext
 * after every successful record_play / record-engagement-bonus call.
 * Renders a tiny, non-intrusive `+$0.003` chip that flies up from the
 * bottom-right with a 6-particle confetti burst, then fades out in
 * 1.2 s. Multiple pops stack vertically so rapid plays don't clobber.
 *
 * Dispatch shape:
 *   window.dispatchEvent(new CustomEvent('ptl:earned', {
 *     detail: { amount: 0.003, kind: 'play' | 'like_bonus' | 'comment_bonus' }
 *   }))
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DollarSign, Heart, MessageCircle } from 'lucide-react';

const KIND_ICON = {
  play: DollarSign,
  like_bonus: Heart,
  comment_bonus: MessageCircle,
  signup_bonus: DollarSign,
  day3_surprise: DollarSign,
};

const KIND_TINT = {
  play: '#00ff88',
  like_bonus: '#FF66AA',
  comment_bonus: '#A855F7',
  signup_bonus: '#FFDA33',
  day3_surprise: '#FFDA33',
};

let popIdSeq = 1;
const TTL_MS = 1200;

export function PtlEarningsPop() {
  const [pops, setPops] = useState([]);

  useEffect(() => {
    const onEarn = (e) => {
      const amount = Number(e.detail?.amount) || 0;
      if (amount <= 0) return;
      const kind = e.detail?.kind || 'play';
      const id = popIdSeq++;
      setPops((prev) => [...prev, { id, amount, kind, ts: Date.now() }]);
      setTimeout(() => {
        setPops((prev) => prev.filter((p) => p.id !== id));
      }, TTL_MS + 80);
    };
    window.addEventListener('ptl:earned', onEarn);
    return () => window.removeEventListener('ptl:earned', onEarn);
  }, []);

  if (!pops.length) return null;

  const overlay = (
    <div
      className="pointer-events-none"
      aria-hidden="true"
      data-testid="ptl-earnings-pop-layer"
      style={{
        position: 'fixed',
        bottom: '96px',
        right: '16px',
        zIndex: 9998,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: '8px',
        alignItems: 'flex-end',
      }}
    >
      <style>{`
        @keyframes ptl-pop-rise {
          0%   { opacity: 0; transform: translateY(20px) scale(0.7); }
          15%  { opacity: 1; transform: translateY(0) scale(1.05); }
          80%  { opacity: 1; transform: translateY(-12px) scale(1); }
          100% { opacity: 0; transform: translateY(-32px) scale(0.95); }
        }
        @keyframes ptl-confetti-1 { 0%{transform:translate(0,0) rotate(0);opacity:1;} 100%{transform:translate(-22px,-32px) rotate(220deg);opacity:0;} }
        @keyframes ptl-confetti-2 { 0%{transform:translate(0,0) rotate(0);opacity:1;} 100%{transform:translate(20px,-36px) rotate(-180deg);opacity:0;} }
        @keyframes ptl-confetti-3 { 0%{transform:translate(0,0) rotate(0);opacity:1;} 100%{transform:translate(-32px,-12px) rotate(120deg);opacity:0;} }
        @keyframes ptl-confetti-4 { 0%{transform:translate(0,0) rotate(0);opacity:1;} 100%{transform:translate(28px,-18px) rotate(-120deg);opacity:0;} }
        @keyframes ptl-confetti-5 { 0%{transform:translate(0,0) rotate(0);opacity:1;} 100%{transform:translate(-12px,-40px) rotate(280deg);opacity:0;} }
        @keyframes ptl-confetti-6 { 0%{transform:translate(0,0) rotate(0);opacity:1;} 100%{transform:translate(14px,-28px) rotate(-260deg);opacity:0;} }
      `}</style>
      {pops.map((p, idx) => (
        <Pop key={p.id} amount={p.amount} kind={p.kind} stackOffset={idx} />
      ))}
    </div>
  );

  return createPortal(overlay, document.body);
}

function Pop({ amount, kind, stackOffset }) {
  const Icon = KIND_ICON[kind] || DollarSign;
  const tint = KIND_TINT[kind] || '#00ff88';
  // Stagger nudge so consecutive pops don't visually overlap.
  const delay = Math.min(stackOffset * 60, 240);
  return (
    <div
      className="relative"
      style={{
        animation: `ptl-pop-rise ${TTL_MS}ms cubic-bezier(0.2,0.8,0.2,1) ${delay}ms both`,
      }}
    >
      {/* Confetti particles */}
      {[1, 2, 3, 4, 5, 6].map((n) => {
        // Spread particles across two accent shades for a punchy burst
        const c = n % 2 === 0 ? tint : '#FFE97A';
        return (
          <span
            key={n}
            className="absolute top-1/2 left-1/2 w-1.5 h-1.5 rounded-full"
            style={{
              background: c,
              boxShadow: `0 0 6px ${c}`,
              animation: `ptl-confetti-${n} 900ms ease-out ${delay}ms forwards`,
              willChange: 'transform, opacity',
            }}
          />
        );
      })}
      {/* Earnings chip */}
      <div
        className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/85 backdrop-blur-sm border font-mono font-bold text-sm tabular-nums"
        style={{
          borderColor: tint,
          color: tint,
          boxShadow: `0 0 18px ${tint}66, 0 4px 12px rgba(0,0,0,0.5)`,
        }}
        data-testid={`ptl-pop-${kind}`}
      >
        <Icon size={13} style={{ color: tint }} />
        +${amount.toFixed(3)}
      </div>
    </div>
  );
}

export default PtlEarningsPop;
